/* eslint-disable @typescript-eslint/no-empty-function */
import React from "react";
import {
  Button,
  Space,
  Row,
  Col,
  Radio,
  Select,
  Dropdown,
  Badge,
  Typography,
  Divider,
  Tag,
  theme,
} from "antd";
import {
  DownloadOutlined,
  CopyOutlined,
  FilterOutlined,
  CalendarOutlined,
  UploadOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Employee } from "../../../types/schedule";
import WeekCalendarSelector from "./WeekCalendarSelector";
import { LocationType } from "../../../components/schedule/modal/location-types";

const { Text } = Typography;

interface WeekViewToolbarProps {
  locations: LocationType[];
  employees: Employee[];
  selectedEmployees: string[];
  onEmployeeChange: (ids: string[]) => void;
  onSearch: (value: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onCurrentWeek: () => void;
  onExportSchedule: () => void;
  onCloneSchedule: () => void;
  onPublishSchedules?: () => void;
  viewMode: "single" | "double";
  onViewModeChange: (mode: "single" | "double") => void;
  // New props for enhanced filtering
  selectedLocation?: string;
  onLocationChange?: (locationId: string) => void;
  selectedDepartment?: string;
  onDepartmentChange?: (departmentId: string) => void;
  showPublishedOnly?: boolean;
  onPublishedFilterChange?: (showPublishedOnly: boolean) => void;
  activeFiltersCount?: number;
  // Week selection props
  currentWeek: Date;
  onWeekChange: (week: Date) => void;
  isManager?: boolean;
  unpublishedCount?: number;
  isPublishing?: boolean;
}

const WeekViewToolbar: React.FC<WeekViewToolbarProps> = ({
  locations,
  employees,
  selectedEmployees,
  onEmployeeChange,
  onPrevWeek,
  onNextWeek,
  onCurrentWeek,
  onExportSchedule,
  onCloneSchedule,
  onPublishSchedules = () => {},
  viewMode,
  onViewModeChange,
  selectedLocation,
  onLocationChange = () => {},
  selectedDepartment = "all",
  onDepartmentChange = () => {},
  showPublishedOnly = false,
  onPublishedFilterChange = () => {},
  activeFiltersCount = 0,
  currentWeek,
  onWeekChange,
  isManager = false,
  unpublishedCount = 0,
  isPublishing = false,
}) => {
  const { token } = theme.useToken();

  const clearAllFilters = () => {
    onLocationChange("all");
    onDepartmentChange("all");
    onEmployeeChange([]);
    onPublishedFilterChange(false);
  };

  const filterMenuItems = [
    {
      key: "clear-all",
      label: (
        <div
          style={{
            padding: "8px 0",
            borderBottom: `1px solid ${token.colorBorder}`,
          }}
        >
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto", color: token.colorError }}
            onClick={clearAllFilters}
          >
            Clear All Filters
          </Button>
        </div>
      ),
    },
    {
      key: "published-filter",
      label: (
        <div style={{ padding: "8px 0" }}>
          <Space>
            <Button
              type={showPublishedOnly ? "primary" : "default"}
              size="small"
              icon={
                showPublishedOnly ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
              onClick={() => onPublishedFilterChange(!showPublishedOnly)}
            >
              {showPublishedOnly ? "Published Only" : "Show All"}
            </Button>
          </Space>
        </div>
      ),
    },
  ];

  const getLocationLabel = () => {
    return (
      locations.find((l) => l.key.toString() === selectedLocation)?.name ||
      "All Locations"
    );
  };

  /*const getDepartmentLabel = () => {
    return (
      defaultDepartments.find((d) => d.value === selectedDepartment)?.label ||
      "All Departments"
    );
  };*/

  return (
    <div style={{ marginBottom: 24 }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Top Row - Location & Department Filters */}
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space size="large" wrap>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <EnvironmentOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <Text
                  strong
                  style={{ color: token.colorText, fontSize: 14, minWidth: 70 }}
                >
                  Location:
                </Text>
                <Select
                  value={selectedLocation ?? locations[0]?.key.toString()}
                  onChange={onLocationChange}
                  style={{ minWidth: 220 }}
                  options={locations.map((emp) => ({
                    value: emp.key.toString(),
                    label: emp.name,
                  }))}
                  placeholder="Select location"
                />
              </div>

              {/*<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <TeamOutlined
                  style={{ color: token.colorSuccess, fontSize: 16 }}
                />
                <Text
                  strong
                  style={{ color: token.colorText, fontSize: 14, minWidth: 90 }}
                >
                  Department:
                </Text>
                <Select
                  value={selectedDepartment}
                  onChange={onDepartmentChange}
                  style={{ minWidth: 200 }}
                  options={mockDepartments}
                  placeholder="Select department"
                />
              </div>*/}

              <Select
                mode="multiple"
                placeholder="Filter employees"
                value={selectedEmployees}
                onChange={onEmployeeChange}
                maxTagCount={2}
                style={{ minWidth: 200, maxWidth: 300 }}
                options={employees.map((emp) => ({
                  label: emp.name,
                  value: emp.id,
                }))}
                size="middle"
              />

              <Dropdown
                menu={{ items: filterMenuItems }}
                trigger={["click"]}
                placement="bottomLeft"
              >
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    borderRadius: 8,
                    borderColor:
                      activeFiltersCount > 0
                        ? token.colorPrimary
                        : token.colorBorder,
                    color:
                      activeFiltersCount > 0
                        ? token.colorPrimary
                        : token.colorTextSecondary,
                  }}
                  size="middle"
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[8, -2]}
                  >
                    Filters
                  </Badge>
                </Button>
              </Dropdown>

              {/* Active Filter Tags - Inline Display */}
              {(selectedLocation !== "all" ||
                selectedDepartment !== "all" ||
                selectedEmployees.length > 0 ||
                showPublishedOnly) && (
                <Space wrap size={[6, 6]} style={{ marginLeft: 8 }}>
                  {selectedLocation !== "all" && (
                    <Tag
                      closable
                      onClose={() => onLocationChange("all")}
                      color="blue"
                      style={{
                        padding: "2px 8px",
                        fontSize: 12,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <EnvironmentOutlined style={{ fontSize: 11 }} />
                      {getLocationLabel()}
                    </Tag>
                  )}

                  {selectedEmployees.length > 0 && (
                    <Tag
                      closable
                      onClose={() => onEmployeeChange([])}
                      color="green"
                      style={{
                        padding: "2px 8px",
                        fontSize: 12,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <TeamOutlined style={{ fontSize: 11 }} />
                      {selectedEmployees.length}{" "}
                      {selectedEmployees.length > 1 ? "s" : ""}
                    </Tag>
                  )}

                  {showPublishedOnly && (
                    <Tag
                      closable
                      onClose={() => onPublishedFilterChange(false)}
                      color="orange"
                      style={{
                        padding: "2px 8px",
                        fontSize: 12,
                        borderRadius: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <EyeOutlined style={{ fontSize: 11 }} />
                      Published
                    </Tag>
                  )}
                </Space>
              )}
            </Space>
          </Col>

          <Col>
            <Space>
              <Radio.Group
                value={viewMode}
                onChange={(e) => onViewModeChange(e.target.value)}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="single">
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  Single Week
                </Radio.Button>
                <Radio.Button value="double">
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  Two Weeks
                </Radio.Button>
              </Radio.Group>
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: 0, borderColor: token.colorBorder }} />

        {/* Bottom Row - Search, Filters & Actions */}
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col flex="auto" style={{ minWidth: 300 }}>
            <Space size="middle" style={{ width: "100%" }}>
              {/*<Input
                prefix={
                  <SearchOutlined style={{ color: token.colorTextTertiary }} />
                }
                placeholder="Search employees by name or role..."
                onChange={(e) => onSearch(e.target.value)}
                style={{
                  maxWidth: 320,
                  borderRadius: 8,
                  borderColor: token.colorBorder,
                }}
                size="middle"
              />

              <Select
                mode="multiple"
                placeholder="Filter employees"
                value={selectedEmployees}
                onChange={onEmployeeChange}
                maxTagCount={2}
                style={{ minWidth: 200, maxWidth: 300 }}
                options={employees.map((emp) => ({
                  label: emp.name,
                  value: emp.id,
                }))}
                size="middle"
              />

              <Dropdown
                menu={{ items: filterMenuItems }}
                trigger={["click"]}
                placement="bottomLeft"
              >
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    borderRadius: 8,
                    borderColor:
                      activeFiltersCount > 0
                        ? token.colorPrimary
                        : token.colorBorder,
                    color:
                      activeFiltersCount > 0
                        ? token.colorPrimary
                        : token.colorTextSecondary,
                  }}
                  size="middle"
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[8, -2]}
                  >
                    Filters
                  </Badge>
                </Button>
              </Dropdown>*/}
            </Space>
          </Col>

          <Col>
            <Space size="middle">
              {/* Week Calendar Selector */}
              <WeekCalendarSelector
                currentWeek={currentWeek}
                onWeekChange={onWeekChange}
                onPrevWeek={onPrevWeek}
                onNextWeek={onNextWeek}
                onCurrentWeek={onCurrentWeek}
              />

              {/* Action Buttons */}
              <Button
                icon={<CopyOutlined />}
                onClick={onCloneSchedule}
                style={{
                  borderRadius: 8,
                  height: 40,
                }}
                size="middle"
              >
                Clone
              </Button>

              {isManager && unpublishedCount > 0 && (
                <Button
                  type="default"
                  icon={<UploadOutlined />}
                  loading={isPublishing}
                  onClick={onPublishSchedules}
                  style={{
                    borderRadius: 8,
                    height: 40,
                  }}
                  size="middle"
                >
                  Publish ({unpublishedCount})
                </Button>
              )}

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={onExportSchedule}
                style={{
                  borderRadius: 8,
                  height: 40,
                }}
                size="middle"
              >
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default WeekViewToolbar;
